import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import BarChartIcon from "@mui/icons-material/BarChart";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { errorToast, handleError } from "../../utils/common";
import { isEqual, parse, startOfDay } from "date-fns";
import { SinglePurchaseDetail } from "./SinglePurchaseDetail";
import { Backdrop, CircularProgress } from "@mui/material";

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 10,
  height: "100%",
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
}));

const IconCircle = styled(Box)(
  ({ theme, bgcolor }: { theme: any; bgcolor: string }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: bgcolor,
  })
);

const ActionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  textAlign: "left",
  justifyContent: "flex-start",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  textTransform: "none",
  borderRadius: 8,
  fontWeight: "normal",
  "&.primary": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

// interface Purchase {
//   invoice: string;
//   distributor: string;
//   amount: number;
//   status: "Paid" | "Pending" | "Failed";
// }

const DistributorsHome: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");
  const [purchaseData, setPurchaseData] = useState([]);
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [activeInvId, setActiveInvId] = useState("");
  const [showSinglePurchaseDetail, setShowSinglePurchaseDetail] =
    useState(false);

  // const recentPurchases: Purchase[] = [
  //   {
  //     invoice: "INV-2023-007",
  //     distributor: "Health Plus Distributors",
  //     amount: 7000,
  //     status: "Paid",
  //   },
  //   {
  //     invoice: "INV-2023-006",
  //     distributor: "MediSupply Co.",
  //     amount: 17820,
  //     status: "Pending",
  //   },
  //   {
  //     invoice: "INV-2023-005",
  //     distributor: "LiveCare Pharma",
  //     amount: 9500,
  //     status: "Paid",
  //   },
  //   {
  //     invoice: "INV-2023-004",
  //     distributor: "MedTech Pharmaceuticals",
  //     amount: 5000,
  //     status: "Failed",
  //   },
  //   {
  //     invoice: "INV-2023-003",
  //     distributor: "Wellness Medical Supplies",
  //     amount: 11880,
  //     status: "Pending",
  //   },
  // ];

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case "paid":
        return { bgcolor: "#e6f7e9", color: "#1e8e3e" };
      case "pending":
        return { bgcolor: "grey.200", color: "text.primary" };
      case "failed":
        return { bgcolor: "#fce8e8", color: "#d32f2f" };
      default:
        return { bgcolor: "#fff8e6", color: "#f9a825" };
    }
  };

  useEffect(() => {
    const getPurchaseData = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers/purchases?location_id=${locationId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setPurchaseData(
            result.data.response?.sort(
              (a: any, b: any) =>
                b.modified_at_timestamp - a.modified_at_timestamp
            )
          );
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    getPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", px: 2, py: 2 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <StatsCard>
            <Typography variant="subtitle2" color="text.secondary">
              Total Purchases
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              {purchaseData?.length}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconCircle theme={theme} bgcolor="#f0ebff">
                <ReceiptIcon sx={{ color: "#0058ff", fontSize: 20 }} />
              </IconCircle>
              {purchaseData.length ? (
                <Button
                  size="small"
                  onClick={() =>
                    navigate(
                      `/distributors/all-purchases?locationId=${locationId}`
                    )
                  }
                >
                  View All
                </Button>
              ) : null}
            </Box>
          </StatsCard>
        </Grid>

        <Grid item xs={6}>
          <StatsCard>
            <Typography variant="subtitle2" color="text.secondary">
              Pending Payments
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              {
                purchaseData?.filter((x: any) => x.payment_status === "pending")
                  ?.length
              }
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconCircle theme={theme} bgcolor="#fff8e6">
                <PaymentIcon sx={{ color: "#f9a825", fontSize: 20 }} />
              </IconCircle>
              <Button
                size="small"
                onClick={() =>
                  navigate(
                    `/distributors/make-payment?locationId=${locationId}&type=all`
                  )
                }
              >
                Pay Now
              </Button>
            </Box>
          </StatsCard>
        </Grid>

        <Grid item xs={6}>
          <StatsCard>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle2" color="text.secondary">
                Amount Due
              </Typography>
              <Typography variant="subtitle2" color="error">
                Today
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              ₹
              {purchaseData
                ?.filter((x: any) =>
                  isEqual(
                    parse(x.due_date, "dd-MM-yyyy", new Date()),
                    startOfDay(new Date())
                  )
                )
                .reduce(
                  (acc, curr: any) => Number(curr.payable_amount) + acc,
                  0
                )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconCircle theme={theme} bgcolor="#fce8e8">
                <CurrencyRupeeIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
              </IconCircle>
              <Button
                size="small"
                onClick={() =>
                  navigate(
                    `/distributors/make-payment?locationId=${locationId}&type=today`
                  )
                }
              >
                View Details
              </Button>
            </Box>
          </StatsCard>
        </Grid>

        <Grid item xs={6}>
          <StatsCard>
            <Typography variant="subtitle2" color="text.secondary">
              Monthly Report
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              {new Date().toLocaleString("default", { month: "long" })}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconCircle theme={theme} bgcolor="#e6f7e9">
                <BarChartIcon sx={{ color: "#1e8e3e", fontSize: 20 }} />
              </IconCircle>
              <Button
                size="small"
                onClick={() =>
                  navigate(
                    `/distributors/generate-reports?locationId=${locationId}`
                  )
                }
              >
                View Reports
              </Button>
            </Box>
          </StatsCard>
        </Grid>
      </Grid>

      {purchaseData?.length ? (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Recent Purchases
            </Typography>
            <Button
              size="small"
              sx={{ color: "#0058ff" }}
              onClick={() =>
                navigate(`/distributors/all-purchases?locationId=${locationId}`)
              }
            >
              View All
            </Button>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice ID</TableCell>
                  <TableCell sx={{ minWidth: "120px" }}>Invoice Date</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseData.map((purchase: any) => {
                  const chipColors = getStatusChipColor(
                    purchase.payment_status
                  );
                  return (
                    <TableRow
                      onClick={() => {
                        setActiveInvId(purchase.purchase_id);
                        setShowSinglePurchaseDetail(true);
                      }}
                      key={purchase.purchase_id}
                    >
                      <TableCell>{purchase.invoice_id}</TableCell>
                      <TableCell>{purchase.invoice_date}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {purchase.supplier_name}
                      </TableCell>
                      <TableCell>
                        ₹{purchase.payable_amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={purchase.payment_status}
                          size="small"
                          sx={{
                            backgroundColor: chipColors.bgcolor,
                            color: chipColors.color,
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Quick Actions
        </Typography>

        <ActionButton
          variant="contained"
          className="primary"
          startIcon={<AddIcon />}
          onClick={() =>
            navigate(`/distributors/add-purchase?locationId=${locationId}`)
          }
        >
          Add New Purchase
        </ActionButton>

        <ActionButton
          variant="outlined"
          startIcon={<PaymentIcon />}
          onClick={() =>
            navigate(`/distributors/make-payment?locationId=${locationId}`)
          }
          sx={{ borderColor: "rgba(0, 0, 0, 0.12)" }}
        >
          Make Payment
        </ActionButton>

        <ActionButton
          variant="outlined"
          startIcon={<BarChartIcon />}
          onClick={() =>
            navigate(`/distributors/generate-reports?locationId=${locationId}`)
          }
          sx={{ borderColor: "rgba(0, 0, 0, 0.12)" }}
        >
          Generate Reports
        </ActionButton>
      </Box>

      {showSinglePurchaseDetail && (
        <SinglePurchaseDetail
          open={showSinglePurchaseDetail}
          handleClose={() => setShowSinglePurchaseDetail(false)}
          activeInvId={activeInvId}
        />
      )}

      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>
    </Box>
  );
};

export default DistributorsHome;
